/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useState, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { StoreContext } from '../Store';
import { useSecurity } from '../Security/security';
import { Input, Select, NeeNorSelect } from '../Input/Input';
import { ValidateAllLoadAdjustments, getAdjustmentsWarningMessage } from '../Util/ValidateLoadAdjustments';
import { generateAdjustmentsObject, incotermRequiresAdjustments, isAdjustmentsRoute } from '../Adjustments/utils';
import ClientIdPage from './ClientIdPage';
import NeeNorModal from './NeerNorModal';
import ControlledGoodsModal from '../ControlledGoodsModal';
import AdjustmentsModal from '../Adjustments/AdjustmentsModal';
import { populateIpaffsModalData } from './Modals/IpaffsModal/utils';
import { isGBImport, isGBexport } from '../../utils/loads';
import { isFreshwaysEori } from '../../utils/companies';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import LoadInfoHeader from './LoadInfoHeader/LoadInfoHeader';
import DocumentsTable from './DocumentsTable/DocumentsTable';
import loadTooltips from './tooltips';
import { jiraTicketValidation, isLoadTypeEPO, isLoadStatusSubmitted } from '../../utils/unifiedLoads';
import { QAStoreContext } from './QAChecklistTab/QAStore';
import { useDropdownOptions } from './queryHooks/useDropdownOptions';
import { getNewLoadNeeNorDropdownV2 } from '../../api/v2/load';
import { Pencil } from '@styled-icons/fa-solid';
import { getPoNumberFromJiraTicket } from '../../api/v2/businessPartner';
import { useBusinessPartnerDetails } from './queryHooks/useBusinessPartner';

const LoadInfo = ({
  customaiteEnabled,
  nextPage,
  checkForDuplicateLoad,
  duplicateCheckLoading = false,
  showRequiredFields,
  updateLoadStatus = null,
  id,
  spreadsheetHandle,
  orderFormCharge,
  headerErrors,
  setAlertOpen,
  setErrorMessage,
  setShowCm8Submission,
  isHybridPort,
  setIsHybridPort,
}) => {
  const store = useContext(StoreContext);
  const qaStore = useContext(QAStoreContext);
  const { validateRole } = useSecurity();
  const [isFreightChargesInco, setIsFreightChargesInco] = useState(false);
  const [checkForm, setCheckForm] = useState(false);
  const formRef = useRef();
  const [freightErrorState, setFreightErrorState] = useState();
  const [showNeeNor, setShowNeeNor] = useState(false);
  const [fetchPoNumberTimer, setFetchPoNumberTimer] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const adjustmentsRef = useRef();

  const { data: consignorDetails } = useBusinessPartnerDetails(store.consignor[0]);
  const { data: consigneeDetails } = useBusinessPartnerDetails(store.consignee[0]);

  const updateNewLoadState = store.updateNewLoadState;
  const setNewLoadDataModified = store.newLoadDataModified[1];
  const setCustomsMoPConfirmation = store.customsMoPConfirmation[1];

  const unifiedLoadDataState = qaStore?.unifiedLoadData?.[0] ?? {};
  const isEPOLoad = isLoadTypeEPO(unifiedLoadDataState?.Headers);

  const isLoadSubmitted = isLoadStatusSubmitted(store.loadStatus[0]);

  const queryClient = useQueryClient();

  const setDataModified = () => {
    setNewLoadDataModified(true);
  };

  const { staticDropdowns, dropdownsByDirection } = useDropdownOptions({ movementType: store.directionSelected[0] });
  const { routes, currencies, incoterms, portProcesses, shippingStatuses } = staticDropdowns.data || {};
  const { despatchCountries, destinationCountries, exitPorts, entryPorts } = dropdownsByDirection.data || {};

  const selectedExit = exitPorts?.find((v) => v.id === store.exit[0]) || {};
  const selectedEntry = entryPorts?.find((v) => v.id === store.entry[0]) || {};

  const showPortModal =
    isHybridPort && (store.GBimport[0] || store.GBexport[0]) && (selectedEntry.hybrid || selectedExit.hybrid);

  const routesWithoutControlledGoods = [
    'EU - EU via GB',
    'GB - EU (mainland)',
    'GB - NI',
    'GB - NI via ROI',
    'GB - ROI',
    'GB - ROI via NI',
    'GB - ROW',
    'NI - GB via ROI',
  ];

  const selectedRoute = routes?.find((route) => String(route.id) === String(store.directionSelected[0]))?.data;

  const showControlledGoodsDropdown = !routesWithoutControlledGoods.includes(selectedRoute);

  // called to debounce fetch of PO number from JIRA ticket
  const initiateFetchPoNumber = (jiraTicket = '') => {
    if (fetchPoNumberTimer) {
      clearTimeout(fetchPoNumberTimer);
    }

    const timer = setTimeout(async () => {
      const poNumberResponse = await queryClient.fetchQuery({
        queryKey: ['getPoNumberFromJiraTicket', jiraTicket.toUpperCase()],
        queryFn: () => getPoNumberFromJiraTicket(jiraTicket.toUpperCase()),
        staleTime: Infinity, // PO number is unlikely to change, so we can cache it indefinitely
      });

      store.poNumber[1](poNumberResponse?.[0]?.poNumber || '');
      clearTimeout(fetchPoNumberTimer);
      setFetchPoNumberTimer(null);
    }, 500);
    setFetchPoNumberTimer(timer);
  };

  /** Validate the whole form and set field errors */
  const stepperRequiredFields = ['directionSelected', 'jobRef', 'invoicenumber'];

  const validateForm = (fieldsToValidate = null) => {
    const newErrors = {};

    Object.keys(headerErrors).forEach((key) => {
      const error = headerErrors[key];

      if ((!fieldsToValidate || fieldsToValidate.includes(error.field)) && error.hasError) {
        newErrors[error.field] = error.hasError;
      }
    });

    setFieldErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isLoadSubmitted) {
      nextPage();
    } else {
      if (!validateForm()) {
        return;
      }

      /** TODO: All the logic regarding Adjustments validation should be refactored in CABS-3771 */
      setFreightErrorState();
      if (event.target.checkValidity()) {
        const fc = store.adjustments[0].find((adj) => adj.name === 'freightCharges');
        const allAdjustmentsAreValid = ValidateAllLoadAdjustments(store.adjustments[0]);

        if (isFreightChargesInco && (fc.amount === '' || fc.currency === '' || fc.amount < 1)) {
          if (fc.amount === '' || fc.currency === '') {
            setShowAdjustmentsModal(true);
            setCheckForm(true);
          } else if (fc.amount < 1) {
            setFreightErrorState('loading');
            store.adjustments[1]((prev) => {
              return prev.map((adjustment) => {
                if (adjustment.name === 'freightCharges') adjustment.error = 'Must be greater than 0.';
                return adjustment;
              });
            });
          }
        } else if (!allAdjustmentsAreValid) {
          setShowAdjustmentsModal(true);
          setCheckForm(true);
        } else {
          const isDuplicateLoad = await checkForDuplicateLoad();
          if (!isDuplicateLoad) {
            nextPage();
          }
        }
      }
    }
  };

  /** Reset direction-dependent fields if their current value is no longer a valid option */
  useEffect(() => {
    const fields = [
      [destinationCountries, store.destination],
      [despatchCountries, store.despatch],
      [exitPorts, store.exit],
      [entryPorts, store.entry],
    ];

    fields.forEach(([list, [value, setter]]) => {
      if (list?.length && !list.some((item) => item.id === value)) {
        setter('');
      }
    });
  }, [dropdownsByDirection.data]);

  /** Re-validate the form whenever header errors change */
  useEffect(() => {
    if (showRequiredFields) {
      validateForm(stepperRequiredFields);
    } else if (Object.keys(fieldErrors).length > 0) {
      validateForm();
    }
  }, [headerErrors, showRequiredFields, setIsHybridPort]);

  useEffect(() => {
    if (checkForm) {
      setTimeout(() => {
        formRef.current.reportValidity();
        setCheckForm(false);
      }, 100);
    }
  }, [checkForm]);

  useEffect(() => {
    if (freightErrorState === 'loading') {
      if (
        store.adjustments[0].reduce((acc, cur) => {
          return acc || !!cur.error;
        }, false)
      )
        setFreightErrorState('loaded');
    }
  }, [store.adjustments[0], freightErrorState]);

  useEffect(() => {
    if (freightErrorState === 'loaded') {
      setFreightErrorState();
      setShowAdjustmentsModal(true);
    }
  }, [freightErrorState]);

  useEffect(() => {
    if (orderFormCharge) {
      setShowAdjustmentsModal(true);
    }
  }, [orderFormCharge]);

  const [disableInputs, setDisableInputs] = useState(true);

  const [ShowPort2Input, setShowPort2Input] = useState(false);
  const [showControlledGoodsModal, setShowControlledGoodsModal] = useState(false);
  const [isAdjustmentsRequired, setIsAdjustmentsRequired] = useState(false);
  const [showAdjustmentsModal, setShowAdjustmentsModal] = useState(false);
  const yesNoOptions = [
    { id: 'Y', data: 'Yes' },
    { id: 'N', data: 'No' },
  ];

  const [eidrAdjustments, setEidrAdjustments] = store.eidrAdjustments;

  const CONSIGNOR_INCOTERMS = ['EXW', 'FCA'];
  const CONSIGNEE_INCOTERMS = ['DPU', 'DAP', 'DDP', 'DDP LITE', 'CFR', 'CIF', 'CPT', 'CIP'];
  const EXIT_PORT_INCOTERMS = ['FAS', 'FOB'];

  const getIncotermLocationSource = (incoterm) => {
    if (CONSIGNOR_INCOTERMS.includes(incoterm)) return 'consignor';
    if (CONSIGNEE_INCOTERMS.includes(incoterm)) return 'consignee';
    if (EXIT_PORT_INCOTERMS.includes(incoterm)) return 'exitPort';
    return null;
  };

  const selectedIncoterm = incoterms?.find((option) => String(option.id) === String(store.selectincoterm[0]))?.data;

  const incotermLocationSource = getIncotermLocationSource(selectedIncoterm);

  const autofillLocation =
    incotermLocationSource === 'consignor'
      ? consignorDetails?.town
      : incotermLocationSource === 'consignee'
        ? consigneeDetails?.town
        : incotermLocationSource === 'exitPort'
          ? exitPorts?.find((port) => String(port.id) === String(store.exit[0]))?.data
          : '';

  const autofillUnavailable =
    isLoadSubmitted ||
    disableInputs ||
    !autofillLocation ||
    (incotermLocationSource === 'consignor' && !store.consignor[0]) ||
    (incotermLocationSource === 'consignee' && !store.consignee[0]) ||
    (incotermLocationSource === 'exitPort' &&
      (!store.exit[0] || store.RowImport[0] || (isEPOLoad && store.GBimport[0])));

  useEffect(() => {
    // RPA from documents table / import takes precedence over saved load data in qaStore
    if (store.newLoadFromFile[0] && store.newLoadFileData[0]?.Headers?.[0]) {
      setHeaderInputs(store.newLoadFileData[0]);
      return;
    }

    if (id && qaStore?.unifiedLoadData?.[0] && Object.keys(qaStore?.unifiedLoadData?.[0])?.length) {
      setHeaderInputs(qaStore.unifiedLoadData[0], true);
    }
  }, [qaStore.unifiedLoadData[0], id, store.newLoadFromFile[0], store.newLoadFileData[0]]);

  useEffect(() => {
    if (!store.directionSelected[0]) return;

    if (despatchCountries?.length === 1 && !store.despatch[0]) {
      store.despatch[1](despatchCountries[0].id);
    }

    if (destinationCountries?.length === 1 && !store.destination[0]) {
      store.destination[1](destinationCountries[0].id);
    }
  }, [store.directionSelected[0], despatchCountries, destinationCountries, store.despatch[0], store.destination[0]]);

  useEffect(() => {
    if (currencies?.length && store.newLoadFileData[0].Headers && store.newLoadFromFile[0]) {
      updateAdjustments(store.newLoadFileData[0]);
    } else if (currencies?.length && eidrAdjustments) {
      updateAdjustments(eidrAdjustments);
    }
  }, [currencies, store.newLoadFileData[0], eidrAdjustments]);

  const updateAdjustments = (returnData) => {
    const adjustments = generateAdjustmentsObject(returnData, currencies);
    adjustmentsRef.current = adjustments;
    store.adjustments[1](adjustments);
  };

  const transformItemsData = (data) => {
    if (!data) return [];
    const returnData = data.map((item) => ({
      commoditycode: item.commodityCode,
      invoiceNumber: item.InvoiceNumber || item.InvoiceNumberV2 || '',
      neeNoridconsignor: item.Consignorid,
      origin: item.origin,
      id: item.id,
      description: item.description,
      packageCount: item.noOfPkgs,
      packageType: item.typeOfPkgs,
      grossMass: item.grossKG,
      netMass: item.netKG,
      itemValue: item.loadValue,
      healthCert: item.healthCert,
      organic: item.organic,
      preferential: ['YES', '1', 1].includes(item.preferential) ? 'YES' : 'NO',
      VAT: item?.VAT,
      dutyLiability: item?.dutyLiability ? item?.dutyLiability : 0,
      vatLiability: item?.vatLiability ? item?.vatLiability : 0,
      customsValue: item?.customsValue ? item?.customsValue : 0,
      exciseProductType: item?.exciseProductType,
      exciseTaxCode: item?.exciseTaxCode,
    }));
    return returnData;
  };

  const setHeaderInputs = (returnData, eidr) => {
    store.certificateCount[1](returnData?.Headers[0]?.certificateCount ?? 0);
    try {
      updateLoadStatus(returnData.Headers[0].loadstatus);
      store.loadId[1](returnData.Headers[0].id);

      // only set the clientID if it is provided in the file - prevents overriding the clientID set by the user
      if (returnData.Headers[0].clientID) {
        store.clientID[1](returnData.Headers[0].clientID);
      }

      if (returnData.Headers[0].companyID) {
        store.companyID[1](returnData.Headers[0].companyID);
      }

      store.clientIDerror[1](returnData.Headers[0].clientIDerror);
      store.declarant[1](returnData.Headers[0].Declarant);
      store.directionSelected[1](returnData.Headers[0].movementTypeId || store.directionSelected[0]);

      store.despatch[1](returnData.Headers[0].CountryofDesptachid || store.despatch[0]);
      store.destination[1](returnData.Headers[0].CountryofDestinationid || store.destination[0]);
      store.exit[1](returnData.Headers[0].ExitPort1id || store.exit[0]);
      store.entry[1](returnData.Headers[0].EntryPort1id || store.entry[0]);
      store.exit2[1](returnData.Headers[0].ExitPort2id || store.exit2[0]);
      store.entry2[1](returnData.Headers[0].EntryPort2id || store.entry2[0]);

      store.consignor[1](returnData.Headers[0].Consignorid);
      store.consignee[1](returnData.Headers[0].Consigneeid);
      store.consignorRPA[1](returnData.Headers[0].Consignorid);
      store.consigneeRPA[1](returnData.Headers[0].Consigneeid);

      store.shipStatus[1](returnData.Headers[0].shipStatus || store.shipStatus[0]);
      store.truck[1](returnData.Headers[0].trucktext || store.truck[0]);
      store.trailer[1](returnData.Headers[0].trailerSeal || store.trailer[0]);
      store.date[1](returnData.Headers?.[0]?.loadingDate?.split('T')[0]);
      store.jobRef[1](returnData.Headers[0].jobRef);
      store.jiraTicket[1](returnData.Headers[0].jiraTicket);
      store.portalRef[1](returnData.Headers[0].portalRef);
      if (returnData.Headers[0].loadTypeDescription) {
        store.loadTypeDescription[1](returnData.Headers[0].loadTypeDescription);
      }

      store.invoicenumber[1](returnData.Headers[0].InvoiceNumber || returnData.Headers[0].InvoiceNumberV2 || '');

      store.poNumber[1](returnData.Headers[0].poNumber);
      store.invoicecurr[1](returnData.Headers[0].InvoiceCurrencyid);
      store.selectincoterm[1](returnData.Headers[0].Incotermid);
      store.incotermLocation[1](returnData.Headers[0].incotermLocation ?? '');
      store.exitPortDefault[1](returnData.Headers[0].exitportdefault);
      store.entryPortDefault[1](returnData.Headers[0].entryportdefault);

      store.GBimport[1](isGBImport(returnData.Headers[0].movementTypeId));
      store.GBexport[1](isGBexport(returnData.Headers[0].movementTypeId));
      store.controlledGoods[1](returnData.Headers[0].controlledGoods);
      store.trailerSeal[1](returnData.Headers[0].trailerSeal);
      store.officeOfDest[1](returnData.Headers[0].officeOfDest);
      store.trailer[1](returnData.Headers[0].trailer);
      store.container[1](returnData.Headers[0].container || store.container[0]);
      returnData.Headers[0]?.parentName &&
        store.newLoadParent[1]({
          parentId: returnData.Headers[0]?.parentId,
          parentName: returnData.Headers[0]?.parentName,
          parentEori: returnData.Headers[0]?.parentEori,
        });

      store.portsModal[1](returnData.Headers[0].portModal || store.portsModal[0]);
      store.multipleConsignors[1](returnData.Headers[0].multipleConsignor || false);
      store.multipleInvoices[1](returnData.Headers[0].multipleInvoice || false);
      store.validatedRowsReadyToSubmit[1](transformItemsData(returnData?.Items));
      store.ipaffsCategories[1](populateIpaffsModalData(returnData?.Items));

      if (eidr) setEidrAdjustments(returnData);
    } catch (error) {
      console.error('Error setting header inputs:', error);
    }
  };

  useEffect(() => {
    try {
      const newDirection = store.directionSelected[0];
      const euToEuViaGB = typeof newDirection === 'string' ? '1' : 1;

      store.GBimport[1](isGBImport(newDirection));
      store.GBexport[1](isGBexport(newDirection));
      store.RowImport[1](isRowImport(newDirection));
      store.RowExport[1](isRowExport(newDirection));
      store.RoiImport[1](isRoiImport(newDirection));

      if (newDirection === '' || newDirection === undefined || newDirection === null) {
        setDisableInputs(true);
        setShowPort2Input(false);
        return;
      }

      setDisableInputs(false);

      setShowPort2Input(newDirection === euToEuViaGB);
    } catch (err) {
      console.error(err);
    }
  }, [store.directionSelected[0]]);

  useEffect(() => {
    if (store.GBimport[0]) {
      const selectedEntryPort = entryPorts?.find((opt) => opt?.id === store.entry[0]);
      if (selectedEntryPort) {
        entryPortChanged(selectedEntryPort);
      }
    }

    if (store.GBexport[0]) {
      const selectedExitPort = exitPorts?.find((opt) => opt?.id === store.exit[0]);
      if (selectedExitPort) {
        exitPortChanged(selectedExitPort);
      }
    }
  }, [
    store.directionSelected[0],
    store.shipStatus[0],
    store.entry[0],
    store.exit[0],
    entryPorts,
    exitPorts,
    store.GBimport[0],
    store.GBexport[0],
  ]);

  const routeChanged = (newSelection) => {
    store.directionSelected[1](newSelection);
    store.incotermLocation[1]('');
  };

  const entryPortChanged = (newSelection) => {
    if (newSelection) {
      const isHybrid = Number(newSelection.hybrid) === 1;
      setIsHybridPort(isHybrid);

      if (isEPOLoad) {
        if (isHybrid && unifiedLoadDataState?.Headers?.[0]?.loadstatus === 'New') {
          store.portsModal[1](store.portsModal[0] ?? '');
        } else {
          store.portsModal[1](newSelection.portProcessId ?? store.portsModal[0]);
        }
      } else if (store.GBimport[0]) {
        if (!newSelection.hybrid) {
          store.portsModal[1]('');
        }
      }

      store.entry[1](newSelection.id);
    } else {
      store.entry[1](0);
    }
  };

  const exitPortChanged = (newSelection) => {
    if (newSelection) {
      const isHybrid = Number(newSelection.hybrid) === 1;
      setIsHybridPort(isHybrid);

      if (isEPOLoad) {
        if (isHybrid && unifiedLoadDataState?.Headers?.[0]?.loadstatus === 'New') {
          store.portsModal[1](store.portsModal[0] ?? '');
        } else {
          store.portsModal[1](newSelection.portProcessId ?? store.portsModal[0]);
        }
      } else if (!store.GBimport[0]) {
        if (!newSelection.hybrid) {
          store.portsModal[1]('');
        }
      }

      store.exit[1](newSelection.id);
    } else {
      store.exit[1](0);
    }
  };

  const consigneeChanged = (value) => {
    updateNewLoadState(value.id, store.consignee[1]);
    setCustomsMoPConfirmation({ confirmed: false, selection: null });
  };

  useEffect(() => {
    setIsAdjustmentsRequired(
      isAdjustmentsRoute(store.directionSelected[0]) && incotermRequiresAdjustments(store.selectincoterm[0]?.toString())
    );
  }, [store.selectincoterm[0], store.directionSelected[0]]);

  useEffect(() => {
    if (currencies?.length) {
      updateAdjustmentsFromIncoTerm();
    }
  }, [store.selectincoterm[0], store.directionSelected[0], currencies]);

  const updateIncoTerm = (data) => {
    store.selectincoterm[1](data);
    updateAdjustmentsFromIncoTerm(data, true);
  };

  const updateAdjustmentsFromIncoTerm = (data, shouldReset) => {
    const incoterm = data?.toString() ?? store.selectincoterm[0]?.toString();

    if (adjustmentsRef.current) {
      store.adjustments[1](
        adjustmentsRef.current.map((adjustment) => {
          if (adjustment.name === 'freightCharges')
            adjustment.required =
              isAdjustmentsRoute(store.directionSelected[0]) && incotermRequiresAdjustments(incoterm);

          if (shouldReset) {
            adjustment.amount = '';
            adjustment.currency = '';
          }

          return adjustment;
        })
      );
      adjustmentsRef.current = null;
    } else {
      store.adjustments[1](
        store.adjustments[0].map((adjustment) => {
          if (adjustment.name === 'freightCharges')
            adjustment.required =
              isAdjustmentsRoute(store.directionSelected[0]) && incotermRequiresAdjustments(incoterm);

          if (shouldReset) {
            adjustment.amount = '';
            adjustment.currency = '';
          }

          return adjustment;
        })
      );
    }
    setIsFreightChargesInco(isAdjustmentsRoute(store.directionSelected[0]) && incotermRequiresAdjustments(incoterm));
  };

  const refreshConsignorCodeDropdown = async (search, skip) => {
    if (!store.directionSelected[0]) return undefined;

    const options = await getNewLoadNeeNorDropdownV2({
      id: store.directionSelected[0],
      type: 'consignor',
      search,
      skip,
      rpaID: store.consignorRPA[0],
      isEPO: isEPOLoad,
    });

    // If selected consignor is not in the new list of options, reset the selected consignor in the store
    if (store.consignor[0] && !options.some((option) => parseInt(option.id) === parseInt(store.consignor[0]))) {
      store.consignor[1]('');
      store.consignorRPA[1]('');
    }

    return options;
  };

  const refreshConsigneeCodeDropdown = async (search, skip) => {
    if (!store.directionSelected[0]) return undefined;

    const options = await getNewLoadNeeNorDropdownV2({
      id: store.directionSelected[0],
      type: 'consignee',
      search,
      skip,
      rpaID: store.consigneeRPA[0],
      isEPO: isEPOLoad,
    });

    // If selected consignee is not in the new list of options, reset the selected consignee in the store
    if (store.consignee[0] && !options.some((option) => parseInt(option.id) === parseInt(store.consignee[0]))) {
      store.consignee[1]('');
      store.consigneeRPA[1]('');
    }

    return options;
  };

  let loadAdjustmentsWarningMessage = '';
  store.adjustments[0]?.forEach((adjustment) => {
    const { warningMessage: adjustmentWarning } = getAdjustmentsWarningMessage(adjustment);
    if (adjustmentWarning) {
      loadAdjustmentsWarningMessage += adjustmentWarning;
    }
  });

  const hasFreshwaysParent = useMemo(() => {
    return isFreshwaysEori(store.newLoadParent[0]?.parentEori);
  }, [store.newLoadParent[0]]);

  return (
    <div>
      {store.clientID[0] === null && !validateRole({ isCustomer: true }) && !store.isEidr[0] ? (
        <ClientIdPage customaiteEnabled={customaiteEnabled} resetIpaffsOnMount={!id && !store.newLoadFromFile[0]} />
      ) : (
        <div className="mb-4" style={{ maxWidth: 2000, margin: '0 auto' }}>
          <section className="floating-box-newload">
            <LoadInfoHeader spreadsheetHandle={spreadsheetHandle} togglesDisabled={isLoadSubmitted} />
            <form onSubmit={handleSubmit} id="newload">
              <div>
                <div id="load-info-form">
                  <section className="inputs-group">
                    <span className="section-title">Movement</span>

                    <Select
                      label="Route"
                      name="direction"
                      data={store.directionSelected[0]}
                      setData={(selection) => routeChanged(selection)}
                      error={fieldErrors.directionSelected}
                      onValueSelected={setDataModified}
                      options={routes}
                      disabled={isLoadSubmitted || store.multipleConsignors[0] || store.multipleInvoices[0]}
                      data-testid="movementType-dropdown"
                      info={loadTooltips.route}
                    />

                    <Select
                      label="Country of Despatch"
                      name="countrydespatch"
                      data={store.despatch[0]}
                      setData={store.despatch[1]}
                      error={fieldErrors.despatch}
                      onValueSelected={setDataModified}
                      options={despatchCountries}
                      filter={1}
                      disabled={isLoadSubmitted || disableInputs}
                      data-testid="countryOfDespatch-dropdown"
                    />

                    <Select
                      label="Country of Destination"
                      name="countrydestination"
                      data={store.destination[0]}
                      setData={store.destination[1]}
                      error={fieldErrors.destination}
                      onValueSelected={setDataModified}
                      options={destinationCountries}
                      filter={1}
                      disabled={isLoadSubmitted || disableInputs}
                      data-testid="countryOfDestination-dropdown"
                    />

                    <Input
                      label="Office of Destination (transit)"
                      name="Office of Destination"
                      type="text"
                      value={store.officeOfDest[0]}
                      error={fieldErrors.officeOfDest}
                      onChange={(e) => updateNewLoadState(e.target.value, store.officeOfDest[1])}
                      disabled={isLoadSubmitted || disableInputs}
                      className="formattedText"
                      data-testid="officeOfDestination-textField"
                      info={loadTooltips.officeOfDestination}
                      noBorders
                    />
                  </section>

                  <section className="inputs-group">
                    <span className="section-title">Direction</span>
                    <Select
                      label="Exit Port 1"
                      name="exitport1"
                      data={store.exit[0]}
                      error={fieldErrors.exit}
                      onChangeAsObject={exitPortChanged}
                      onValueSelected={setDataModified}
                      options={exitPorts}
                      filter={1}
                      disabled={
                        isLoadSubmitted || disableInputs || store.RowImport[0] || (isEPOLoad && store.GBimport[0])
                      }
                      data-testid="exitPort1-dropdown"
                    />

                    <Select
                      label="Entry Port 1"
                      name="entryport1"
                      data={store.entry[0]}
                      error={fieldErrors.entry}
                      onChangeAsObject={entryPortChanged}
                      options={entryPorts}
                      onValueSelected={setDataModified}
                      filter={1}
                      disabled={
                        isLoadSubmitted || disableInputs || store.RowExport[0] || (isEPOLoad && store.GBexport[0])
                      }
                      data-testid="entryPort1-dropdown"
                    />

                    {showPortModal && (
                      <Select
                        label="Port Model"
                        name="mixedPortsModalentry"
                        data={store.portsModal[0]}
                        setData={store.portsModal[1]}
                        error={fieldErrors.portsModal}
                        onValueSelected={setDataModified}
                        options={portProcesses}
                        filter={1}
                        disabled={isLoadSubmitted || disableInputs}
                        info={loadTooltips.portModal}
                      />
                    )}

                    <div
                      style={{
                        display: ShowPort2Input === true ? 'block' : 'none',
                      }}
                    >
                      <Select
                        label="Exit Port 2"
                        name="exitport2"
                        data={store.exit2[0]}
                        setData={store.exit2[1]}
                        error={fieldErrors.exit2}
                        onValueSelected={setDataModified}
                        options={entryPorts}
                        filter={1}
                        data-testid="exitPort2-dropdown"
                        disabled={isLoadSubmitted}
                      />

                      <Select
                        label="Entry Port 2"
                        name="entryport2"
                        data={store.entry2[0]}
                        setData={store.entry2[1]}
                        error={fieldErrors.entry2}
                        onValueSelected={setDataModified}
                        options={exitPorts}
                        filter={1}
                        data-testid="entryPort2-dropdown"
                        disabled={isLoadSubmitted}
                      />
                    </div>

                    <div
                      className={`d-flex flex-column w-100 ${!store.consignor[0] || consignorDetails?.addressDetailsComplete ? '' : 'field-has-warnings'}`}
                    >
                      <NeeNorSelect
                        label="Consignor Code"
                        name="consignor"
                        data={store.consignor[0]}
                        setData={(value) => updateNewLoadState(value, store.consignor[1])}
                        error={fieldErrors.consignor}
                        disabled={isLoadSubmitted || disableInputs || store.multipleConsignors[0]}
                        refresh={refreshConsignorCodeDropdown}
                        dependencies={[store.directionSelected[0], store.processedRpaId[0], store.consignorRPA[0]]}
                        dataTestId="consignorCode-dropdown"
                        info={() =>
                          loadTooltips.consignor(
                            consignorDetails?.blocked,
                            consignorDetails?.standingAuthority,
                            store.clientIdAllowSAonNeeNors[0]
                          )
                        }
                      />
                      {!store.consignor[0] || consignorDetails?.addressDetailsComplete ? (
                        <></>
                      ) : (
                        <span className="input-warning-box mt-1">Address details are incomplete.</span>
                      )}
                    </div>

                    <div
                      className={`d-flex flex-column w-100 ${!store.consignee[0] || consigneeDetails?.addressDetailsComplete ? '' : 'field-has-warnings'}`}
                    >
                      <NeeNorSelect
                        label="Consignee Code"
                        name="consignee"
                        data={store.consignee[0]}
                        error={fieldErrors.consignee}
                        onChangeAsObject={consigneeChanged}
                        disabled={isLoadSubmitted || disableInputs}
                        refresh={refreshConsigneeCodeDropdown}
                        dependencies={[store.directionSelected[0], store.processedRpaId[0], store.consigneeRPA[0]]}
                        dataTestId="consigneeCode-dropdown"
                        info={() =>
                          loadTooltips.consignee(
                            consigneeDetails?.blocked,
                            consigneeDetails?.standingAuthority,
                            store.clientIdAllowSAonNeeNors[0]
                          )
                        }
                      />
                      {!store.consignee[0] || consigneeDetails?.addressDetailsComplete ? (
                        <></>
                      ) : (
                        <span className="input-warning-box mt-1">Address details are incomplete.</span>
                      )}
                    </div>
                    {!isLoadSubmitted && (
                      <div
                        className="more-help"
                        onClick={() => setShowNeeNor(true)}
                        style={{ marginTop: '12px', fontWeight: 'normal' }}
                      >
                        Add NeeNor?
                      </div>
                    )}
                  </section>

                  <section className="inputs-group">
                    <span className="section-title">Identification</span>
                    <Select
                      label="Shipping Status"
                      name="shipStatus"
                      data={store.shipStatus[0]}
                      setData={store.shipStatus[1]}
                      error={fieldErrors.shipStatus}
                      onValueSelected={setDataModified}
                      options={shippingStatuses}
                      disabled={isLoadSubmitted || disableInputs}
                      data-testid="shippingStatus-dropdown"
                      info={loadTooltips.shippingStatus}
                    />
                    <Input
                      label="Truck / Trailer Number"
                      name="truck"
                      type="text"
                      value={store.truck[0]}
                      error={fieldErrors.truck}
                      onChange={(e) => {
                        updateNewLoadState(e.target.value, store.truck[1]);
                      }}
                      disabled={isLoadSubmitted || disableInputs}
                      className="formattedText"
                      data-testid="truckTrailerNumber-textField"
                      noBorders
                    />
                    <Input
                      label="Trailer Seal Number"
                      name="trailerNo"
                      type="text"
                      value={store.trailerSeal[0]}
                      error={fieldErrors.trailerSeal}
                      onChange={(e) => updateNewLoadState(e.target.value, store.trailerSeal[1])}
                      disabled={isLoadSubmitted || disableInputs}
                      className="formattedText"
                      data-testid="trailerSealNumber-textField"
                      noBorders
                    />
                    <Input
                      label="Date Of Loading"
                      name="date"
                      type="date"
                      value={store.date[0]}
                      error={fieldErrors.date}
                      onChange={(e) => {
                        updateNewLoadState(e.target.value, store.date[1]);
                      }}
                      disabled={isLoadSubmitted || disableInputs}
                      data-testid="dateOfLoading-date"
                      noBorders
                    />
                    <Input
                      label="Container"
                      name="container"
                      type="text"
                      value={store.container[0]}
                      error={fieldErrors.container}
                      onChange={(e) => {
                        updateNewLoadState(e.target.value, store.container[1]);
                      }}
                      disabled={isLoadSubmitted || disableInputs}
                      className="formattedText"
                      data-testid="container-textField"
                      info={loadTooltips.container}
                      noBorders
                    />
                  </section>

                  <section className="inputs-group">
                    <span className="section-title">Commercial</span>
                    <Input
                      label="Jira Ticket"
                      name="jiraTicket"
                      type="text"
                      className="formattedText"
                      value={store.jiraTicket[0]}
                      error={fieldErrors.jiraTicket}
                      onChange={(e) => {
                        updateNewLoadState(e.target.value, store.jiraTicket[1]);
                        const validationError = jiraTicketValidation(e.target.value, store.isJiraTicketRequired[0]);

                        // if valid string and parent is Freshways, fetch PO number for ticket
                        if (!validationError && isFreshwaysEori(store.newLoadParent[0]?.parentEori)) {
                          initiateFetchPoNumber(e.target.value);
                        }
                      }}
                      onBlur={() => store.jiraTicket[1]((prev) => prev?.toUpperCase())}
                      disabled={isLoadSubmitted || disableInputs}
                      data-testid="jiraTicket-textField"
                      noBorders
                    />

                    <Input
                      label="Job Reference"
                      name="jobref"
                      type="text"
                      value={store.jobRef[0]}
                      error={fieldErrors.jobRef}
                      onChange={(e) => {
                        updateNewLoadState(e.target.value, store.jobRef[1]);
                      }}
                      disabled={isLoadSubmitted || disableInputs}
                      className="formattedText"
                      data-testid="jobReference-textField"
                      noBorders
                    />

                    <Input
                      label="Invoice Number"
                      name="invoicenumber"
                      type="text"
                      value={store.invoicenumber[0]}
                      error={fieldErrors.invoicenumber}
                      onChange={(e) => {
                        updateNewLoadState(e.target.value, store.invoicenumber[1]);
                      }}
                      disabled={isLoadSubmitted || disableInputs || store.multipleInvoices[0]}
                      className="formattedText"
                      data-testid="invoiceNumber-textField"
                      noBorders
                    />

                    {hasFreshwaysParent && (
                      <Input
                        label={
                          <>
                            PO Number {fetchPoNumberTimer && <LoadingSpinner size="1rem" style={{ marginLeft: 10 }} />}
                          </>
                        }
                        name="poNumber"
                        type="text"
                        value={store.poNumber[0]}
                        error={fieldErrors.poNumber}
                        onChange={(e) => {
                          updateNewLoadState(e.target.value, store.poNumber[1]);
                        }}
                        disabled={isLoadSubmitted || disableInputs}
                        className="formattedText"
                        data-testid="poNumber-textField"
                        noBorders
                      />
                    )}

                    <Select
                      label="Invoice Currency"
                      name="invoicecurr"
                      data={store.invoicecurr[0]}
                      setData={store.invoicecurr[1]}
                      error={fieldErrors.invoicecurr}
                      onValueSelected={setDataModified}
                      options={currencies}
                      disabled={isLoadSubmitted || disableInputs}
                      data-testid="invoiceCurrency-dropdown"
                    />
                    <Select
                      label="Incoterm"
                      name="incotermcode"
                      data={store.selectincoterm[0]}
                      error={fieldErrors.selectincoterm || fieldErrors.adjustments}
                      setData={updateIncoTerm}
                      onValueSelected={setDataModified}
                      options={incoterms}
                      disabled={isLoadSubmitted || disableInputs}
                      data-testid="incoterm-dropdown"
                      info={loadTooltips.incoterm}
                    />

                    <div className={`input-outer-container`}>
                      <div className="input-label" style={{ marginBottom: '0px' }}>
                        <button
                          type="button"
                          className="invoice-add-btn green-text"
                          onClick={(e) => {
                            setShowAdjustmentsModal(true);
                          }}
                          disabled={disableInputs}
                        >
                          <span
                            className="text-decoration-underline"
                            style={{ fontSize: '0.9rem', fontStyle: 'italic' }}
                          >
                            Adjustments
                          </span>
                          <span>{loadTooltips.adjustments(isAdjustmentsRequired)}</span>
                        </button>
                      </div>
                      {loadAdjustmentsWarningMessage && (
                        <span className="input-warning-box mt-1">{loadAdjustmentsWarningMessage}</span>
                      )}
                    </div>

                    <Input
                      label="Incoterm Location"
                      name="incoLoco"
                      type="text"
                      value={store.incotermLocation[0]}
                      error={fieldErrors.incotermLocation}
                      onChange={(e) => updateNewLoadState(e.target.value, store.incotermLocation[1])}
                      disabled={isLoadSubmitted || disableInputs}
                      className="formattedText"
                      noBorders
                      suffix={
                        !isLoadSubmitted && (
                          <button
                            type="button"
                            className="invoice-add-btn green-text py-1"
                            style={{ alignSelf: 'center' }}
                            disabled={autofillUnavailable}
                            title={
                              autofillUnavailable
                                ? 'Autofill unavailable - update header screen or manually enter location'
                                : 'Autofill Incoterm Location'
                            }
                            onClick={() => {
                              updateNewLoadState(autofillLocation, store.incotermLocation[1]);
                              setDataModified();
                            }}
                          >
                            <Pencil width={'1.1rem'} height={'1.1rem'} style={{ alignSelf: 'center' }} />
                          </button>
                        )
                      }
                    />

                    {showControlledGoodsDropdown && (
                      <>
                        <Select
                          label={store.RowImport[0] ? 'Frontier Required' : 'Controlled Goods'}
                          name="controlledgoods"
                          data={store.controlledGoods[0]}
                          setData={store.controlledGoods[1]}
                          error={fieldErrors.controlledGoods}
                          options={yesNoOptions}
                          disabled={isLoadSubmitted || disableInputs}
                          data-testid="controlledGoods-dropdown"
                          onChange={(e) => store.controlledGoods[1](e.target.value)}
                          onValueSelected={setDataModified}
                          info={<div className="important-info" onClick={() => setShowControlledGoodsModal(true)} />}
                        />

                        <ControlledGoodsModal
                          header="Controlled Goods"
                          size="md"
                          showButtons={false}
                          animation={false}
                          show={showControlledGoodsModal}
                          onHide={() => setShowControlledGoodsModal(false)}
                          subTitle="Select Yes in the drop-down if your Consignment contains any of these items - click on the information icons for more details."
                        />
                      </>
                    )}

                    <NeeNorModal
                      onHide={() => setShowNeeNor(false)}
                      show={showNeeNor}
                      size="xl"
                      showButtons={false}
                      animation={false}
                      hideHeader={true}
                      isRow={store.RowImport[0] || store.RowExport[0]}
                    />

                    <AdjustmentsModal
                      adjustments={store.adjustments[0]}
                      setAdjustments={store.adjustments[1]}
                      currencyOptions={currencies}
                      show={showAdjustmentsModal}
                      onHide={() => {
                        setShowAdjustmentsModal(false);
                      }}
                      ref={formRef}
                      readOnly={isLoadSubmitted}
                    />

                    <div className="flex-grow-1" />
                    <button
                      className="blue-button-newload"
                      type="submit"
                      style={{ justifySelf: 'end' }}
                      disabled={duplicateCheckLoading}
                    >
                      Items
                    </button>
                  </section>

                  {store.details[0][0].Description}
                </div>
                <div></div>
              </div>
            </form>
          </section>
          {store.loadId[0] && (
            <section className="floating-box-newload mt-3">
              <DocumentsTable
                setAlertOpen={setAlertOpen}
                setErrorMessage={setErrorMessage}
                route={selectedRoute}
                setShowCm8Submission={setShowCm8Submission}
              />
            </section>
          )}
        </div>
      )}
    </div>
  );
};

const isRowImport = (id) => {
  switch (id) {
    case 13:
    case '13':
      return true;
    default:
      return false;
  }
};

const isRowExport = (id) => {
  switch (id) {
    case 12:
    case '12':
      return true;
    default:
      return false;
  }
};

const isRoiImport = (id) => {
  switch (id) {
    case 5:
    case '5':
    case 6:
    case '6':
    case 7:
    case '7':
      return true;
    default:
      return false;
  }
};

export default LoadInfo;
